<script setup>
/**
 * The entry point for selling a flight: an action in the contact panel that records what
 * the customer is asking for and opens a trip against them.
 *
 * It sits in the contact panel because a trip is a fact about the CUSTOMER, not about the
 * conversation - see docs/travelport/trip-and-conversation-design.md. The conversation is
 * passed along as where the trip started, and is genuinely optional: an agent opening a
 * trip from the contact page, outside any conversation, is ordinary.
 *
 * Self-gating on the travel flag, like VoiceCallButton gates on voice inboxes - the action
 * row stays free of travel-specific conditions it should not have to know about.
 *
 * This only captures the brief. Searching and sending a shortlist is the separate "Send
 * flight options" action, because a brief is often taken before anyone is ready to quote.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import TravelTripsAPI from 'dashboard/api/travel/trips';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  // The conversation the trip started in, when there is one.
  conversationId: {
    type: [Number, String],
    default: null,
  },
});

// The panel that hosts this button re-reads its list on `created`, so a new trip appears
// where the agent just made it rather than on the next conversation open.
const emit = defineEmits(['created']);

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { enabledLanguages } = useConfig();

const shouldRender = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.TRAVEL)
);

const emptyBrief = () => ({
  origin: '',
  destination: '',
  departureDate: '',
  returnDate: '',
  passengers: 1,
  preferredTime: '',
  language: 'en',
  notes: '',
});

const dialogRef = ref(null);
const isSaving = ref(false);
const brief = ref(emptyBrief());

const languageOptions = computed(() =>
  (enabledLanguages ?? []).map(language => ({
    value: language.iso_639_1_code,
    label: language.name,
  }))
);

const timeWindowOptions = computed(() =>
  ['ANY', 'MORNING', 'AFTERNOON', 'EVENING'].map(window => ({
    value: window === 'ANY' ? '' : window.toLowerCase(),
    label: t(`TRAVEL.SEND_OPTIONS.TIME_WINDOWS.${window}`),
  }))
);

// The three fields the trip cannot exist without, and the same three
// TravelTrip::REQUIREMENTS_SCHEMA requires. Airport codes are three letters there too, so
// a typo is caught here rather than as a 422.
const canSubmit = computed(
  () =>
    brief.value.origin.trim().length === 3 &&
    brief.value.destination.trim().length === 3 &&
    !!brief.value.departureDate &&
    !isSaving.value
);

const openDialog = () => dialogRef.value?.open();

// `undefined` rather than empty strings: the schema declares additionalProperties false
// and types the optional fields as string-or-null, so an empty string for a date is a 422
// rather than "not stated".
const requirementsPayload = () => ({
  origin: brief.value.origin.trim().toUpperCase(),
  destination: brief.value.destination.trim().toUpperCase(),
  departure_date: brief.value.departureDate,
  return_date: brief.value.returnDate || undefined,
  preferred_time: brief.value.preferredTime || undefined,
  language: brief.value.language,
  notes: brief.value.notes || undefined,
  passengers: [{ type: 'ADT', count: Number(brief.value.passengers) }],
});

const onSubmit = async () => {
  if (!canSubmit.value) return;

  isSaving.value = true;
  try {
    await TravelTripsAPI.create({
      contact_id: props.contactId,
      conversation_id: props.conversationId || undefined,
      requirements: requirementsPayload(),
    });
    useAlert(t('TRAVEL.TRIP.START.SUCCESS'));
    emit('created');
    // A second trip for the same customer is a different journey, so the next open starts
    // from a blank brief rather than the last one's.
    brief.value = emptyBrief();
    dialogRef.value?.close();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TRAVEL.TRIP.START.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <span class="contents">
    <!-- Labelled, not icon-only. This is the panel's only way to create a trip, and a bare
         glyph with a hover tooltip is both easy to miss and unreachable without a mouse -
         `v-tooltip` is a directive, so it gives the button no accessible name. -->
    <NextButton
      v-if="shouldRender"
      icon="i-ph-airplane-takeoff"
      :label="$t('TRAVEL.TRIP.START.LABEL')"
      slate
      faded
      sm
      @click="openDialog"
    />
    <Dialog
      v-if="shouldRender"
      ref="dialogRef"
      width="lg"
      overflow-y-auto
      :close-on-click-outside="false"
      :title="$t('TRAVEL.TRIP.START.TITLE')"
      :description="$t('TRAVEL.TRIP.START.DESC')"
      :confirm-button-label="$t('TRAVEL.TRIP.START.SUBMIT')"
      :disable-confirm-button="!canSubmit"
      :is-loading="isSaving"
      @confirm="onSubmit"
    >
      <form
        class="grid w-full grid-cols-1 gap-3 sm:grid-cols-2"
        @submit.prevent="onSubmit"
      >
        <Input
          v-model="brief.origin"
          :label="$t('TRAVEL.TRIP.START.ORIGIN')"
          :placeholder="$t('TRAVEL.FLIGHT_SEARCH.FORM.ORIGIN_PLACEHOLDER')"
          maxlength="3"
        />
        <Input
          v-model="brief.destination"
          :label="$t('TRAVEL.TRIP.START.DESTINATION')"
          :placeholder="$t('TRAVEL.FLIGHT_SEARCH.FORM.DESTINATION_PLACEHOLDER')"
          maxlength="3"
        />
        <Input
          v-model="brief.departureDate"
          type="date"
          :label="$t('TRAVEL.TRIP.START.DEPART')"
        />
        <Input
          v-model="brief.returnDate"
          type="date"
          :label="$t('TRAVEL.TRIP.START.RETURN')"
        />
        <Input
          v-model="brief.passengers"
          type="number"
          min="1"
          max="9"
          :label="$t('TRAVEL.TRIP.START.PASSENGERS')"
        />
        <label
          class="flex flex-col gap-1 mb-0 text-sm font-medium text-n-slate-12"
        >
          {{ $t('TRAVEL.TRIP.START.PREFERRED_TIME') }}
          <Select
            v-model="brief.preferredTime"
            :options="timeWindowOptions"
            class="!w-full [&>select]:w-full"
          />
        </label>
        <label
          class="flex flex-col gap-1 mb-0 text-sm font-medium text-n-slate-12"
        >
          {{ $t('TRAVEL.TRIP.START.LANGUAGE') }}
          <Select
            v-model="brief.language"
            :options="languageOptions"
            class="!w-full [&>select]:w-full"
          />
        </label>
        <Input
          v-model="brief.notes"
          class="sm:col-span-2"
          :label="$t('TRAVEL.TRIP.START.NOTES')"
          :placeholder="$t('TRAVEL.SEND_OPTIONS.NOTES_PLACEHOLDER')"
        />
      </form>
    </Dialog>
  </span>
</template>
